import { useCallback, useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';

import { vnextApiClient } from '@/services/vnext-api';
import { ApprovalStatus, ReportApproval } from '@/types/report';

interface UseReportApprovalsParams {
  reportId: string | null | undefined;
  /** Derived initial status from the report itself — avoids an extra request */
  initialStatus?: ApprovalStatus | null;
  /** Whether to fetch approval history on mount */
  autoFetchHistory?: boolean;
}

export interface UseReportApprovalsResult {
  status: ApprovalStatus | null;
  approvals: ReportApproval[];
  isLoading: boolean;
  isMutating: boolean;
  error: Error | null;
  approve: (notes?: string) => Promise<ReportApproval | null>;
  revoke: (notes?: string) => Promise<ReportApproval | null>;
  refetch: () => Promise<void>;
}

/**
 * Hook for reading and mutating a report's approval state.
 *
 * Approval state is an immutable append-only log — each approve/revoke call
 * adds a new row. The latest row determines the current status.
 */
export function useReportApprovals({
  reportId,
  initialStatus = null,
  autoFetchHistory = false,
}: UseReportApprovalsParams): UseReportApprovalsResult {
  const [status, setStatus] = useState<ApprovalStatus | null>(initialStatus);
  const [approvals, setApprovals] = useState<ReportApproval[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Keep local status in sync when the parent re-renders with a new initialStatus
  useEffect(() => {
    setStatus(initialStatus);
  }, [initialStatus]);

  const refetch = useCallback(async () => {
    if (!reportId) {
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const history = await vnextApiClient.getReportApprovals(reportId);
      setApprovals(history);
      // Latest record (history[0] is most recent from the backend) defines status
      if (history.length === 0) {
        setStatus((prev) => prev ?? null);
      } else {
        setStatus(history[0].status === 'approved' ? 'approved' : 'revoked');
      }
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load approval history');
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    if (autoFetchHistory && reportId) {
      refetch();
    }
  }, [autoFetchHistory, reportId, refetch]);

  const approve = useCallback(
    async (notes?: string): Promise<ReportApproval | null> => {
      if (!reportId) {
        return null;
      }
      try {
        setIsMutating(true);
        setError(null);
        const record = await vnextApiClient.approveReport(reportId, notes);
        setApprovals((prev) => [record, ...prev]);
        setStatus('approved');
        notifications.show({
          title: 'Report approved',
          message: 'Clients can now view this report.',
          color: 'green',
        });
        return record;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to approve report');
        setError(e);
        notifications.show({
          title: 'Failed to approve report',
          message: e.message,
          color: 'red',
        });
        throw e;
      } finally {
        setIsMutating(false);
      }
    },
    [reportId]
  );

  const revoke = useCallback(
    async (notes?: string): Promise<ReportApproval | null> => {
      if (!reportId) {
        return null;
      }
      try {
        setIsMutating(true);
        setError(null);
        const record = await vnextApiClient.revokeReportApproval(reportId, notes);
        setApprovals((prev) => [record, ...prev]);
        setStatus('revoked');
        notifications.show({
          title: 'Approval revoked',
          message: 'Clients can no longer view this report.',
          color: 'yellow',
        });
        return record;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to revoke report approval');
        setError(e);
        notifications.show({
          title: 'Failed to revoke approval',
          message: e.message,
          color: 'red',
        });
        throw e;
      } finally {
        setIsMutating(false);
      }
    },
    [reportId]
  );

  return {
    status,
    approvals,
    isLoading,
    isMutating,
    error,
    approve,
    revoke,
    refetch,
  };
}
